import { el } from './dom.js';
import { ct } from './i18n.js';
import { Popover } from './popover.js';

/**
 * Colour picker.
 *
 * The native `<input type="color">` opens the operating system's palette:
 * inconsistent from one platform to the next, unrelated to the theme, and with
 * no pointer at all towards the colours genuinely usable in a Discord embed.
 *
 * The first version replaced it with a grid of swatches, which solved the
 * consistency problem and created another: a server that wants *its* blue, the
 * one on its banner, could not reach it without knowing its hex code. Twenty-
 * eight rectangles is a menu, not a picker.
 *
 * So this is a real one - a saturation/value field over a hue rail, the shape
 * every design tool has used for thirty years and the one people already know
 * how to drive. The presets stay, below, because a server picking an accent
 * colour for the first time is better served by "Blurple" than by a gradient.
 */

/** Discord's official colours, plus a range of common shades. */
const PRESETS: { hex: string; key: string; label: string }[] = [
  { hex: '#5865F2', key: 'cBlurple', label: 'Blurple' },
  { hex: '#57F287', key: 'cGreen', label: 'Green' },
  { hex: '#FEE75C', key: 'cYellow', label: 'Yellow' },
  { hex: '#EB459E', key: 'cFuchsia', label: 'Fuchsia' },
  { hex: '#ED4245', key: 'cRed', label: 'Red' },
  { hex: '#FFFFFF', key: 'cWhite', label: 'White' },
  { hex: '#1ABC9C', key: 'cTurquoise', label: 'Turquoise' },
  { hex: '#3498DB', key: 'cSky', label: 'Sky blue' },
  { hex: '#9B59B6', key: 'cAmethyst', label: 'Amethyst' },
  { hex: '#E67E22', key: 'cCarrot', label: 'Carrot' },
  { hex: '#95A5A6', key: 'cLightGrey', label: 'Light grey' },
  { hex: '#23272A', key: 'cNearBlack', label: 'Near black' },
];

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const FALLBACK = '#5865F2';

/* ------------------------------------------------------------ colour maths */

interface Hsv {
  /** 0-360 */
  h: number;
  /** 0-1 */
  s: number;
  /** 0-1 */
  v: number;
}

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const part = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

function hexToHsv(hex: string): Hsv {
  const [r, g, b] = hexToRgb(hex).map((n) => n / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: max === 0 ? 0 : delta / max, v: max };
}

function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];

  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/** Black or white, whichever stays readable on the given colour. */
function contrastColor(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  // ITU-R BT.601 coefficients: the usual approximation of perceived luminance.
  return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#000000' : '#FFFFFF';
}

/**
 * Turns an element into a draggable surface reporting 0-1 coordinates.
 *
 * Pointer events rather than mouse events, so a finger on a phone drives it too,
 * and `setPointerCapture` so a drag that leaves the element keeps tracking -
 * without it, dragging past the edge of the gradient drops the colour on
 * whatever pixel the pointer happened to be over when it left.
 */
function trackDrag(surface: HTMLElement, onMove: (x: number, y: number) => void): void {
  const report = (event: PointerEvent) => {
    const box = surface.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    const y = Math.min(1, Math.max(0, (event.clientY - box.top) / box.height));
    onMove(x, y);
  };

  surface.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    surface.setPointerCapture(event.pointerId);
    report(event);
  });

  surface.addEventListener('pointermove', (event) => {
    if (!surface.hasPointerCapture(event.pointerId)) return;
    report(event);
  });

  surface.addEventListener('pointerup', (event) => {
    surface.releasePointerCapture(event.pointerId);
  });
}

/* ---------------------------------------------------------------- the field */

export class ColorPicker {
  private readonly swatch: HTMLElement;
  private readonly label: HTMLElement;

  constructor(private readonly input: HTMLInputElement) {
    const host = el('div', { class: 'w-full' });
    input.replaceWith(host);

    // The original field becomes the carrier of the value, invisible but still
    // serialised by the form.
    input.type = 'hidden';
    host.append(input);

    const trigger = el('button', {
      class: 'color-trigger',
      attrs: { type: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': 'false' },
    });

    this.swatch = el('span', { class: 'color-trigger-swatch' });
    this.label = el('span', { class: 'color-trigger-value' });

    trigger.append(this.swatch, this.label);
    trigger.append(el('span', { class: 'color-trigger-caret', text: '▾' }));
    host.append(trigger);

    // The popover attaches itself to the trigger: no reference to keep.
    new Popover({ trigger, width: 'auto', build: (close) => this.buildPanel(close) });

    /*
     * Redrawn whenever the field changes from outside. A page that fills the
     * field after the picker exists — the embed editor restoring a saved
     * colour, the card gallery's "Surprise me" — dispatches `input`; without
     * this the swatch kept the colour it was born with, and a customer read
     * "the colour resets when I save".
     */
    input.addEventListener('input', () => this.render());

    this.render();
  }

  get value(): string {
    return HEX_PATTERN.test(this.input.value) ? this.input.value.toUpperCase() : FALLBACK;
  }

  private set(hex: string): void {
    this.input.value = hex.toUpperCase();
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.render();
  }

  private render(): void {
    const hex = this.value;
    this.swatch.style.backgroundColor = hex;
    this.label.textContent = hex;
  }

  private buildPanel(close: () => void): HTMLElement {
    const wrapper = el('div', { class: 'color-panel' });

    /*
     * The working colour is held in HSV, not read back from the hex.
     *
     * A grey has no meaningful hue - every hue produces the same grey - so
     * deriving the rail position from the hex would snap the hue to red the
     * moment saturation reaches zero, and dragging back out of the corner would
     * come back red instead of the blue you started from.
     */
    let hsv = hexToHsv(this.value);

    // ── Saturation and value ────────────────────────────────────────────────
    const field = el('div', {
      class: 'color-field',
      attrs: { role: 'application', 'aria-label': ct('cSaturation', 'Saturation and brightness') },
    });
    const fieldThumb = el('span', { class: 'color-thumb' });
    field.append(fieldThumb);

    // ── Hue ─────────────────────────────────────────────────────────────────
    const rail = el('div', {
      class: 'color-rail',
      attrs: { role: 'slider', 'aria-label': ct('cHue', 'Hue') },
    });
    const railThumb = el('span', { class: 'color-thumb' });
    rail.append(railThumb);

    const preview = el('div', { class: 'color-preview' });
    const hexInput = el('input', {
      class: 'color-hex',
      attrs: {
        type: 'text',
        maxlength: '7',
        spellcheck: 'false',
        placeholder: FALLBACK,
        'aria-label': ct('hexCode', 'Hex code'),
      },
    });

    /** Repaints every part of the panel from the working colour. */
    const paint = (updateHexField = true) => {
      const hex = hsvToHex(hsv);

      field.style.backgroundColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });
      fieldThumb.style.left = `${String(hsv.s * 100)}%`;
      fieldThumb.style.top = `${String((1 - hsv.v) * 100)}%`;
      fieldThumb.style.backgroundColor = hex;

      railThumb.style.left = `${String((hsv.h / 360) * 100)}%`;
      railThumb.style.backgroundColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });

      preview.style.backgroundColor = hex;
      preview.style.color = contrastColor(hex);
      preview.textContent = hex;

      // Not while the field has focus: rewriting it under the cursor makes a
      // half-typed code impossible to finish.
      if (updateHexField && document.activeElement !== hexInput) hexInput.value = hex;

      this.set(hex);
    };

    trackDrag(field, (x, y) => {
      hsv = { ...hsv, s: x, v: 1 - y };
      paint();
    });

    trackDrag(rail, (x) => {
      hsv = { ...hsv, h: x * 360 };
      paint();
    });

    wrapper.append(field, rail);

    // ── Preview, hex and eyedropper ─────────────────────────────────────────
    const row = el('div', { class: 'color-row' });

    hexInput.addEventListener('click', (event) => event.stopPropagation());
    hexInput.addEventListener('input', () => {
      // A missing hash is tolerated: it is the single most common way to paste
      // a colour from anywhere else.
      const raw = hexInput.value.trim();
      const candidate = raw.startsWith('#') ? raw : `#${raw}`;
      if (!HEX_PATTERN.test(candidate)) return;

      hsv = hexToHsv(candidate);
      paint(false);
    });

    row.append(preview, hexInput);

    /*
     * The eyedropper, where the browser has one.
     *
     * It is the fastest way to match a colour already on screen - a banner, a
     * logo - and it exists in Chrome and Edge but not in Firefox or Safari, so
     * the button is added only when it will work rather than offered and then
     * failing.
     */
    if ('EyeDropper' in window) {
      const dropper = el('button', {
        class: 'color-tool',
        attrs: {
          type: 'button',
          'aria-label': ct('cEyedropper', 'Pick from screen'),
          title: ct('cEyedropper', 'Pick from screen'),
        },
      });
      dropper.textContent = '⌖';

      dropper.addEventListener('click', (event) => {
        event.stopPropagation();

        interface EyeDropperResult {
          sRGBHex: string;
        }
        const Ctor = (window as unknown as Record<string, unknown>).EyeDropper as new () => {
          open: () => Promise<EyeDropperResult>;
        };

        void new Ctor()
          .open()
          .then((result) => {
            if (!HEX_PATTERN.test(result.sRGBHex)) return;
            hsv = hexToHsv(result.sRGBHex);
            paint();
          })
          // Cancelling the eyedropper rejects. That is not an error, and it must
          // not reach the console as one.
          .catch(() => undefined);
      });

      row.append(dropper);
    }

    wrapper.append(row);

    // ── Presets ─────────────────────────────────────────────────────────────
    const grid = el('div', { class: 'color-presets' });
    for (const preset of PRESETS) {
      const cell = el('button', {
        class: 'color-preset',
        attrs: {
          type: 'button',
          title: `${ct(preset.key, preset.label)} · ${preset.hex}`,
          'aria-label': ct(preset.key, preset.label),
        },
      });
      cell.style.backgroundColor = preset.hex;
      if (preset.hex.toUpperCase() === this.value) cell.dataset.current = 'true';

      cell.addEventListener('click', (event) => {
        event.stopPropagation();
        this.set(preset.hex);
        close();
      });
      grid.append(cell);
    }
    wrapper.append(grid);

    paint();
    return wrapper;
  }
}

/** Equips every field marked `data-color-input`. */
export function upgradeColorInputs(root: ParentNode = document): void {
  for (const input of root.querySelectorAll<HTMLInputElement>('input[data-color-input]')) {
    if (input.dataset.colorReady === 'true') continue;
    input.dataset.colorReady = 'true';
    new ColorPicker(input);
  }
}
